"use client";

import Link from "next/link";
import NumberFlow from "@number-flow/react";
import { Icon } from "@/components/ui/icon";
import { formatShortDate } from "@/lib/date/format-short-date";
import type {
  CreditBalanceResponse,
  SubscriptionResponse,
} from "../../hooks/use-subscription";

function formatRange(start: string | null, end: string | null): string | null {
  if (!start || !end) return null;
  return `${formatShortDate(start)} - ${formatShortDate(end)}`;
}

interface UsageCardProps {
  credits: CreditBalanceResponse | null;
  subscription: SubscriptionResponse | null;
  /**
   * Renders a link through to the credit usage breakdown. Opt-in so the
   * explainer modal, which embeds this card, doesn't offer a navigation
   * that would strand the user behind an open dialog.
   */
  breakdownHref?: string;
}

export function UsageCard({
  credits,
  subscription,
  breakdownHref,
}: UsageCardProps) {
  const balance = credits?.balance ?? 0;
  const total = credits?.total_allocation ?? 0;
  const nextCycle = credits?.next_cycle_allocation ?? total;
  const used = Math.max(total - balance, 0);
  const usedPercent = total > 0 ? (used / total) * 100 : 0;
  const showRenewalHint = nextCycle !== total && total > 0;

  const planSlug = subscription?.plan.slug ?? "free";
  const cadence = subscription?.billing_cadence;
  const periodPhrase =
    planSlug === "free"
      ? "lifetime"
      : cadence === "annual"
        ? "this year"
        : "this month";
  const range = formatRange(
    credits?.period_start ?? null,
    credits?.period_end ?? null,
  );
  const periodBadge = range ?? (planSlug === "free" ? "Lifetime" : null);

  const renewalDate = credits?.period_end
    ? new Date(credits.period_end).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface-inset p-3 dark:border-white/10">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium text-heading dark:text-white">
          Team credit usage
        </span>
        {periodBadge ? (
          <span className="inline-flex items-center rounded-md border border-border bg-contrast/5 px-1.5 py-0.5 text-xs leading-4 text-contrast/70 dark:border-white/10 dark:bg-white/5 dark:text-white/75">
            {periodBadge}
          </span>
        ) : null}
        {breakdownHref ? (
          <Link
            href={breakdownHref}
            data-testid="credit-breakdown-link"
            className="ml-auto inline-flex items-center gap-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            View details
            <Icon name="IconChevronRight" size={12} />
          </Link>
        ) : null}
      </div>

      {/* Score panel */}
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface-primary p-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:border-white/10 dark:bg-white/2">
        <div className="flex items-center gap-1.5">
          <div className="relative flex size-[35px] shrink-0 items-center justify-center overflow-hidden rounded-[7px] border border-border bg-contrast/4 dark:border-white/5 dark:bg-white/4">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[rgba(255,175,83,0.1)]" />
            <Icon
              name="IconCoin1"
              size={20}
              className="relative text-[#c2710a] dark:text-[#FFAF53]"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium leading-none text-heading dark:text-white">
              Total credit usage
            </span>
            <span className="flex items-baseline gap-1 text-[13px] leading-none">
              <span className="font-medium text-heading dark:text-white">
                <NumberFlow value={used} /> of {total.toLocaleString()} credits
              </span>
              <span className="text-contrast/40 dark:text-white/40">used · {periodPhrase}</span>
            </span>
            {showRenewalHint && (
              <span className="text-[11px] leading-none text-contrast/35 dark:text-white/30">
                Renews at {nextCycle.toLocaleString()} credits
                {renewalDate ? ` on ${renewalDate}` : ""}
              </span>
            )}
          </div>
        </div>

        <div className="h-1.5 w-full overflow-hidden rounded-full bg-contrast/15 dark:bg-white/[0.15]">
          <div
            className="h-full rounded-full bg-heading transition-all duration-500 dark:bg-white"
            style={{
              width: `${Math.min(usedPercent, 100)}%`,
              minWidth: usedPercent > 0 ? "8px" : undefined,
            }}
          />
        </div>
      </div>
    </div>
  );
}
